import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Archive, Copy, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { Modal } from "../../components/Modal";
import { GroupPill } from "../../components/GroupPill";
import { StageBadge } from "../../components/StageBadge";
import { ExportMenu } from "../../components/ExportMenu";
import { formatApiErrorDetail, formatPT, groupLabel } from "../../lib/format";
import SignupAttempts from "./SignupAttempts";

const SORTS = [
  { v: "newest", label: "Newest first" },
  { v: "oldest", label: "Oldest first" },
  { v: "name", label: "Name A–Z" },
];

function SignupTabs({ tab, onChange }) {
  const cls = (k) => `px-4 py-2 font-sans text-sm border-b-2 -mb-px ${tab === k ? "border-brass text-ink" : "border-transparent text-stone hover:text-ink"}`;
  return (
    <div className="flex gap-2 border-b border-hairline no-print" data-testid="signups-tabs">
      <button onClick={() => onChange("signups")} className={cls("signups")} data-testid="signups-tab-list">Class Signups</button>
      <button onClick={() => onChange("attempts")} className={cls("attempts")} data-testid="signups-tab-attempts">Sign-up attempts</button>
    </div>
  );
}

export default function ReefSignups() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "attempts" ? "attempts" : "signups";
  const setTab = (t) => setParams(t === "attempts" ? { tab: "attempts" } : {});
  return (
    <div className="space-y-8">
      <SignupTabs tab={tab} onChange={setTab} />
      {tab === "attempts" ? <SignupAttempts /> : <SignupsList />}
    </div>
  );
}

function SignupsList() {
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [groupBy, setGroupBy] = useState(false);
  const [moveSel, setMoveSel] = useState({});

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([api.get("/admin/signups"), api.get("/classes")]);
    setRows(r.data);
    setClasses(c.data);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c])), [classes]);

  const filtered = useMemo(() => {
    let out = rows.filter((r) =>
      filter === "all" ? true : filter === "none" ? !r.class_id : r.class_id === filter
    );
    out = [...out];
    if (sort === "oldest") out.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "name") out.sort((a, b) => (a.full_name || "").toLowerCase().localeCompare((b.full_name || "").toLowerCase()));
    else out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return out;
  }, [rows, filter, sort]);

  const counts = useMemo(() => {
    const c = { all: rows.length, none: rows.filter((r) => !r.class_id).length };
    classes.forEach((cl) => { c[cl.id] = rows.filter((r) => r.class_id === cl.id).length; });
    return c;
  }, [rows, classes]);

  const toggleHandled = async (id) => {
    try {
      await api.post(`/admin/signups/${id}/handled`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const archive = async () => {
    try {
      await api.post(`/admin/signups/${confirming.id}/archive`);
      toast.success("Archived — kept in the records, hidden from this list.");
      setConfirming(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const move = async (row) => {
    const target = moveSel[row.id];
    try {
      await api.patch(`/admin/signups/${row.id}`, { class_id: target });
      const oldLabel = row.class_id ? groupLabel(classById[row.class_id]) : "No group";
      toast.success(`${row.full_name}: ${oldLabel} → ${groupLabel(classById[target])}`);
      setMoveSel((s) => ({ ...s, [row.id]: "" }));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyEmails = async () => {
    const seen = new Set();
    const out = [];
    filtered.forEach((r) => {
      const k = (r.email || "").toLowerCase();
      if (k && !seen.has(k)) { seen.add(k); out.push(r.email); }
    });
    await navigator.clipboard.writeText(out.join(", "));
    toast.success(`${out.length} email${out.length === 1 ? "" : "s"} copied — ready for BCC`);
  };

  const chip = (key, label, count) => (
    <button
      key={key}
      onClick={() => setFilter(key)}
      className={`filter-chip px-3 py-1 text-xs font-sans border ${filter === key ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`}
      data-testid={`signup-filter-${key}`}
    >
      {label} ({count})
    </button>
  );

  const printCols = [
    { key: "full_name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "note", label: "Note" },
    { key: "group", label: "Group", render: (r) => (r.class_id ? groupLabel(classById[r.class_id]) : "No group yet") },
    { key: "stage", label: "Stage" },
    { key: "created_at", label: "Date added", render: (r) => formatPT(r.created_at) },
  ];

  const Card = ({ r }) => (
    <div key={r.id} className="paper-card p-5" data-testid={`signup-row-${r.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-serif text-xl text-ink leading-tight">{r.full_name}</p>
            <StageBadge stage={r.stage} />
            {r.handled && <StatusMark status="approved" labelOverride="Handled" />}
          </div>
          <p className="font-mono text-xs text-stone mt-1 break-all">
            {r.email}{r.phone ? ` · ${r.phone}` : ""}
          </p>
          <p className="font-mono text-xs text-stone mt-0.5" data-testid={`signup-date-${r.id}`}>
            Date added — {formatPT(r.created_at)}
          </p>
          {r.note && <p className="font-sans text-sm text-ink/75 mt-2">{r.note}</p>}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <GroupPill cls={r.class_id ? classById[r.class_id] : null} />
            {r.class_id && (
              <Link to={`/reef/classes/${r.class_id}`} className="btn-ghost text-xs" data-testid={`open-class-link-${r.id}`}>
                Open class <ArrowRight size={12} />
              </Link>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            {r.handled ? (
              <button onClick={() => toggleHandled(r.id)} className="btn-ghost text-sm" data-testid={`unhandle-signup-${r.id}`}>Undo handled</button>
            ) : (
              <button onClick={() => toggleHandled(r.id)} className="btn-outline text-sm" data-testid={`handle-signup-${r.id}`}>
                <Check size={14} /> Mark handled
              </button>
            )}
            <button onClick={() => setConfirming(r)} className="btn-ghost text-sm text-garnet" data-testid={`archive-signup-${r.id}`}>
              <Archive size={14} /> Archive
            </button>
          </div>
          <div className="flex items-center gap-2 w-full">
            <select
              className="field-input !py-1.5 text-xs flex-1"
              value={moveSel[r.id] || ""}
              onChange={(e) => setMoveSel((s) => ({ ...s, [r.id]: e.target.value }))}
              data-testid={`group-select-${r.id}`}
            >
              <option value="">Group…</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{groupLabel(c)}</option>
              ))}
            </select>
            <button
              onClick={() => move(r)}
              disabled={!moveSel[r.id] || moveSel[r.id] === r.class_id}
              className="btn-outline text-xs disabled:opacity-40"
              data-testid={`move-group-${r.id}`}
            >
              Move
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const groupsForGrouping = [
    ...classes.filter((c) => filtered.some((r) => r.class_id === c.id)).map((c) => ({ cls: c, rows: filtered.filter((r) => r.class_id === c.id) })),
    ...(filtered.some((r) => !r.class_id) ? [{ cls: null, rows: filtered.filter((r) => !r.class_id) }] : []),
  ];

  return (
    <div className="space-y-8" data-testid="reef-signups">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">From the /join page — flyer &amp; word-of-mouth traffic</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Class Signups</h1>
          <p className="font-sans text-sm text-stone mt-2 max-w-xl">People who asked for a spot. Organized by group and date added.</p>
        </div>
        <div className="flex items-center gap-3 no-print">
          <button onClick={copyEmails} className="btn-ghost text-sm" data-testid="copy-signup-emails">
            <Copy size={14} /> Copy all emails
          </button>
          <ExportMenu
            list="signups"
            params={{ group: filter, sort }}
            title="Class Signups"
            columns={printCols}
            rows={filtered}
          />
        </div>
      </div>

      <div className="space-y-3 no-print">
        <div className="chip-row" data-testid="signup-filter-chips">
          {chip("all", "All", counts.all)}
          {classes.map((c) => chip(c.id, c.admin_label || c.title, counts[c.id] || 0))}
          {chip("none", "No group yet", counts.none)}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <select className="field-input !py-1.5 text-sm w-full sm:w-48" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="signup-sort">
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          <label className="flex items-center gap-2 font-sans text-sm text-ink cursor-pointer">
            <input type="checkbox" checked={groupBy} onChange={(e) => setGroupBy(e.target.checked)} data-testid="group-by-toggle" />
            Group by class
          </label>
        </div>
      </div>

      {groupBy ? (
        <div className="space-y-8">
          {groupsForGrouping.map(({ cls, rows: grows }) => (
            <div key={cls ? cls.id : "none"} data-testid={`signup-group-${cls ? cls.id : "none"}`}>
              <div className="mb-3">
                <div className="h-1 w-full mb-2" style={{ background: cls ? `var(--group-${cls.color || "blue"})` : "#E5DECF" }} />
                <p className="font-serif text-xl text-ink">
                  {cls ? groupLabel(cls) : "No group yet"}{" "}
                  <span className="font-sans text-xs text-stone">{grows.length} signup{grows.length === 1 ? "" : "s"}</span>
                </p>
              </div>
              <div className="space-y-3">{grows.map((r) => <Card key={r.id} r={r} />)}</div>
            </div>
          ))}
          {groupsForGrouping.length === 0 && <p className="font-sans text-sm text-stone">Nothing here yet.</p>}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => <Card key={r.id} r={r} />)}
          {filtered.length === 0 && <p className="font-sans text-sm text-stone">Nothing here yet.</p>}
        </div>
      )}

      <Modal
        open={!!confirming}
        onClose={() => setConfirming(null)}
        title="Archive this signup?"
        testid="archive-signup-modal"
        footer={
          <>
            <button onClick={() => setConfirming(null)} className="btn-ghost text-sm" data-testid="archive-cancel">Cancel</button>
            <button onClick={archive} className="btn-brass text-sm" data-testid="archive-confirm">Archive</button>
          </>
        }
      >
        <p className="font-sans text-sm text-ink/80">
          {confirming ? `${confirming.full_name} will be hidden from this list. The record stays in the books — nothing is deleted.` : ""}
        </p>
      </Modal>
    </div>
  );
}
